/**
 * cr-media-gallery-nav.js
 *
 * Drives the custom .slider-nav--prev / .slider-nav--next buttons
 * added to product-media-gallery.liquid.
 *
 * On click: finds the active media item, then programmatically clicks the
 * adjacent thumbnail button — reusing media-gallery.js setActiveMedia logic.
 *
 * Active state: the `active` class is present when navigation in that
 * direction is possible (removed when at the first / last item).
 */
(function () {
  function initGalleryNav(gallery) {
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;

    const prevBtn = viewer.querySelector('.slider-nav--prev');
    const nextBtn = viewer.querySelector('.slider-nav--next');
    if (!prevBtn && !nextBtn) return;

    const thumbnailContainer = gallery.querySelector('[id^="GalleryThumbnails"]');

    // Returns an ordered list of navigable items.
    // Prefers thumbnail strip entries; falls back to the media items themselves.
    function getItems() {
      if (thumbnailContainer) {
        return [...thumbnailContainer.querySelectorAll('[data-target]')];
      }
      return [...viewer.querySelectorAll('[data-media-id]')];
    }

    function getActiveIndex() {
      const activeMedia = viewer.querySelector('[data-media-id].is-active');
      if (!activeMedia) return 0;
      const items = getItems();
      if (thumbnailContainer) {
        return items.findIndex((t) => t.dataset.target === activeMedia.dataset.mediaId);
      }
      return items.indexOf(activeMedia);
    }

    function updateButtons() {
      const total = getItems().length;
      const idx = getActiveIndex();
      if (prevBtn) prevBtn.classList.toggle('active', idx > 0);
      if (nextBtn) nextBtn.classList.toggle('active', idx < total - 1);
    }

    function navigate(direction) {
      const items = getItems();
      const newIdx = getActiveIndex() + direction;
      if (newIdx < 0 || newIdx >= items.length) return;

      if (thumbnailContainer) {
        items[newIdx].querySelector('button')?.click();
      } else {
        // Fallback when no thumbnail strip: scroll the media list directly
        items[newIdx].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        navigate(-1);
        // Let the DOM update before recalculating
        requestAnimationFrame(updateButtons);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        navigate(1);
        requestAnimationFrame(updateButtons);
      });
    }

    // Stay in sync when a thumbnail is clicked directly
    if (thumbnailContainer) {
      thumbnailContainer.querySelectorAll('[data-target] button').forEach((btn) => {
        btn.addEventListener('click', () => requestAnimationFrame(updateButtons));
      });
    }

    // Stay in sync on mobile scroll-based navigation
    viewer.addEventListener('slideChanged', () => requestAnimationFrame(updateButtons));

    // Set initial state
    updateButtons();
  }

  function init() {
    document.querySelectorAll('media-gallery').forEach(initGalleryNav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
